import { combineReducers, configureStore } from '@reduxjs/toolkit'
import type { PersistedState } from 'redux-persist'
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

// Core reducers
import alertsReducer from './core/alerts'
import { operationalAlertsReducer } from './core/operational-alerts'
import privacyReducer from './core/privacy'
import globalSettingsReducer from './core/settings'
import pubSidebarReducer from './features/pub-sidebar'
import preferencesReducer from './slices/preferences-slice'

// App reducers
import { userProfileReducer as profileReducer } from './slices/user-profile-slice'
import helperTextReducer from './features/helperText'

// Feature reducers
import articlesReducer from './features/articles'
import galleryReducer from './features/gallery'
import installersReducer from './features/installers'
import locationsReducer from './features/locations'
import productsReducer from './features/products'
import productTableReducer from './features/productTable'
import servicesReducer from './features/services'
import vehiclesReducer from './features/vehicles'

// Middleware
import { criticalFieldResetMiddleware } from './middleware/dependency/critical-field-reset'
import { optimisticMiddleware } from './middleware/optimistic/optimistic-updates'
import { syncRecentlyViewedMiddleware } from './middleware/sync/sync-recently-viewed'
import { actionValidationMiddleware } from './middleware/validation/action-validation'
import { stateValidationMiddleware } from './middleware/validation/state-validation'

// Shared slices
import pendingDataReducer from './shared/pendingData'
import recentlyViewedReducer from './shared/recently-viewed'

// Additional feature reducers
import bookingReducer, { bookingPersistedDefaults } from './features/booking/bookingSlice'
import cartReducer, { cartPersistedDefaults } from './features/cart/cartSlice'
import lastVehicleReducer from './features/booking/lastVehicleSlice'
import optimisticReducer from './features/optimistic/optimisticSlice'
import productVariantTableReducer from './features/product-variants/product-variant-table-slice'
import productVariantsReducer from './features/product-variants/product-variants-slice'
import projectCategoriesReducer from './features/project-categories'
import uploadsReducer from './features/uploads'

// Configure persist for services
const servicesPersistConfig = {
  key: 'services',
  storage,
  whitelist: ['items'] // Only persist the items array
}

/**
 * RootState Type
 * Inferred type from store.getState()
 */
export type RootState = ReturnType<typeof store.getState>

/**
 * AppDispatch Type
 * Inferred type from store.dispatch
 */
export type AppDispatch = typeof store.dispatch

const rootReducer = combineReducers({
  alerts: alertsReducer,
  operationalAlerts: operationalAlertsReducer,
  globalSettings: globalSettingsReducer,
  privacy: privacyReducer,
  preferences: preferencesReducer,
  cart: cartReducer,
  products: productsReducer,
  productVariants: productVariantsReducer,
  productVariantTable: productVariantTableReducer,
  articles: articlesReducer,
  gallery: galleryReducer,
  services: persistReducer(servicesPersistConfig, servicesReducer),
  recentlyViewed: recentlyViewedReducer,
  pubSidebar: pubSidebarReducer,
  projectCategories: projectCategoriesReducer,
  uploads: uploadsReducer,
  locations: locationsReducer,
  installers: installersReducer,
  helperText: helperTextReducer,
  profile: profileReducer,
  optimistic: optimisticReducer,
  pendingData: pendingDataReducer,
  productTable: productTableReducer,
  vehicles: vehiclesReducer,
  booking: bookingReducer,
  lastVehicle: lastVehicleReducer
})

/**
 * Backfill booking.persisted and cart.persisted keys that a stored blob predates.
 *
 * redux-persist's default reconciler (autoMergeLevel1) merges only one level
 * deep, so `booking.persisted` / `cart.persisted` are restored from storage
 * WHOLESALE: the nested defaults in initialState are never merged in. A key
 * added to that object after a visitor's blob was written therefore rehydrates
 * as `undefined` for them, and any reducer writing into it throws mid-dispatch.
 * That is invisible to a fresh browser (which is why tests and new visitors
 * never saw it) and silently bricks the control for everyone who used the
 * wizard/cart before the key existed. `serviceTiers` hit exactly this and left
 * the tier pickers inert.
 *
 * This runs on every rehydrate and is idempotent, so it covers keys added in
 * the future too, without needing a version bump each time.
 */
type PersistedRoot = PersistedState & {
  booking?: { persisted?: Partial<typeof bookingPersistedDefaults> }
  cart?: { persisted?: Partial<typeof cartPersistedDefaults> }
}

const migrate = async (state: PersistedState): Promise<PersistedState> => {
  const root = state as PersistedRoot | undefined
  if (!root?.booking?.persisted && !root?.cart?.persisted) return state
  return {
    ...root,
    ...(root.booking?.persisted && {
      booking: {
        ...root.booking,
        persisted: { ...bookingPersistedDefaults, ...root.booking.persisted }
      }
    }),
    ...(root.cart?.persisted && {
      cart: {
        ...root.cart,
        persisted: { ...cartPersistedDefaults, ...root.cart.persisted }
      }
    })
  } as PersistedState
}

const persistConfig = {
  key: 'root',
  storage,
  migrate,
  whitelist: ['booking', 'auth', 'lastVehicle', 'cart'] // Persist booking, auth, last-used vehicle, and cart
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

/**
 * Redux Store Configuration
 */
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          'profile/setUserPaymentProfiles' // Ignore payment profiles action due to Date serialization
        ]
      }
    }).concat(
      syncRecentlyViewedMiddleware,
      actionValidationMiddleware,
      stateValidationMiddleware,
      optimisticMiddleware,
      criticalFieldResetMiddleware
    )
})

export const persistor = persistStore(store)
